import type {
  IAmbiente,
  IAnswer,
  IIndicators,
  IPrompts,
  IQuestion,
  IScheda,
  ISymbol,
} from './models';

// Bump quando i default cambiano: una config con version diversa viene scartata.
export const CONFIG_VERSION = 18;

// Le domande si riconoscono per `code` (stabile, dall'admin: Q01..Q10 come nel seed v1), mai per
// posizione: nel percorso parziale le voci attive sono un sottoinsieme.
export const VISUAL_QUESTION_CODE = 'Q09'; // D9 · Domanda visiva (tavole A/B/C)
export const GIRO_CASA_CODE = 'Q10'; // D10 · Giro della casa (sottodomande per stanza)
export const ANTI_DUP_HINT_CODES: readonly string[] = ['Q04', 'Q08', GIRO_CASA_CODE];
export const sameCode = (a: string | undefined, b: string): boolean =>
  undefined !== a && a.trim().toUpperCase() === b.toUpperCase();
export const isVisualQuestion = (q: { code?: string } | undefined): boolean =>
  sameCode(q?.code, VISUAL_QUESTION_CODE);

// Limiti di scelta per tavola nella domanda visiva (qcard + completamento in interview.ts):
// non tutte le immagini vanno votate, solo al massimo queste per categoria.
export const MAX_SI_PER_TAVOLA = 2;
export const MAX_NO_PER_TAVOLA = 1;

// Sotto questa soglia il testo incollato in "Le risposte" è troppo corto per un'analisi
// utile (es. incollato per errore): non abilita Genera Profilo da solo, serve anche un allegato.
export const MIN_ANSWERS_CHARS = 200;

export const ACCEPTED_FILE_TYPES: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

export const SESSION_DURATIONS: readonly string[] = [
  '',
  '30 min',
  '45 min',
  '1h',
  '1h 15min',
  '1h 30min',
  '1h 45min',
  '2h',
  '2h 30min',
  '3h',
];

// Titolo di gruppo per la pulsantiera non verbale (§1 doc DEV_v6): stesso raggruppamento
// usato sia nei pulsanti (qcard) sia nella legenda (interview).
export const SYMBOL_GROUP_TITLES: Record<'verde' | 'rosso' | 'giallo' | 'nota', string> = {
  verde: 'IL CORPO CONFERMA',
  rosso: 'IL CORPO SMENTISCE',
  giallo: 'DA CHIARIRE',
  nota: 'ALTRO',
};

export const SYMBOLS: readonly ISymbol[] = [
  {
    s: '★',
    label: 'Marker identitario',
    desc: 'Elemento ad alta carica emotiva o identitaria',
    auto: true,
    color: '#C0533E',
  },
  {
    s: '↑',
    label: 'Intensificazione',
    desc: 'Aumento di tono — coinvolgimento positivo',
    auto: true,
    color: '#2a7d4f',
  },
  {
    s: '↓',
    label: 'Deflazione',
    desc: 'Calo di tono — disagio o conflitto latente',
    auto: true,
    color: '#c0392b',
  },
  {
    s: '⋯',
    label: 'Pausa significativa',
    desc: 'Esitazione, elaborazione interiore',
    auto: true,
    color: '#7b68ee',
  },
  {
    s: '~',
    label: 'Ambivalenza',
    desc: 'Oscillazione, contraddizione interna',
    auto: true,
    color: '#d68910',
  },
  {
    s: 'V1',
    label: 'SI ACCENDE',
    desc: 'Occhi e voce si animano, si sporge, i gesti crescono sul tema — il corpo conferma: alza il peso del tema.',
    auto: false,
    color: '#2a7d4f',
    group: 'verde',
  },
  {
    s: 'V2',
    label: 'LEGAME',
    desc: "Tocca/indica con affetto un oggetto o ambiente, si commuove, indugia su un ricordo — elemento identitario: candidato ★ del profilo.",
    auto: false,
    color: '#2a7d4f',
    group: 'verde',
  },
  {
    s: 'R1',
    label: 'SI SPEGNE',
    desc: 'Si ritrae, si chiude, smette di gesticolare, monosillabi — il corpo smentisce: se il testo è positivo → incongruenza.',
    auto: false,
    color: '#c0392b',
    group: 'rosso',
  },
  {
    s: 'R2',
    label: 'A DISAGIO',
    desc: 'Si irrigidisce, si agita, evita lo sguardo sul tema — zona sensibile: se contraddice il testo → incongruenza.',
    auto: false,
    color: '#c0392b',
    group: 'rosso',
  },
  {
    s: 'G1',
    label: 'SÌ SOLO A PAROLE',
    desc: 'Dice «va bene / sì sì» ma viso e corpo non seguono — assenso di cortesia: sempre candidato a chiarimento.',
    auto: false,
    color: '#d68910',
    group: 'giallo',
  },
  {
    s: 'G2',
    label: 'GUARDA IL PARTNER',
    desc: "Cerca l'approvazione dell'altro prima di rispondere — risposta forse non autonoma: verifica separata.",
    auto: false,
    color: '#d68910',
    group: 'giallo',
  },
  {
    s: 'G3',
    label: 'PARTNER IN DISACCORDO',
    desc: 'I due divergono visibilmente sul tema — alimenta la Mappa delle tensioni.',
    auto: false,
    color: '#d68910',
    group: 'giallo',
  },
  {
    s: 'G4',
    label: 'QUALCOSA NON TORNA',
    desc: 'Sensazione netta che detto e mostrato non coincidano — basta da solo a rendere il tema incongruente.',
    auto: false,
    color: '#d68910',
    group: 'giallo',
  },
  {
    s: 'N1',
    label: 'NOTA',
    desc: 'Qualsiasi osservazione fattuale che i pulsanti non catturano (max una frase).',
    auto: false,
    color: '#6b7280',
    group: 'nota',
  },
];

export const DEFAULT_QUESTIONS: IQuestion[] = [
  {
    id: 1,
    code: 'Q01',
    tag: 'Ricordi',
    title: 'Cosa portare, cosa lasciare',
    text: `Delle case in cui hai vissuto (anche di vacanza), cosa ti porteresti nella nuova casa e cosa invece non vorresti ritrovare? E per quale ragione? Vale anche per gli altri componenti della famiglia?`,
    sondaggi: [],
  },
  {
    id: 2,
    code: 'Q02',
    tag: 'Vita quotidiana',
    title: 'Le giornate tipo',
    text: `Raccontateci come trascorreranno le giornate tipo le persone in casa durante tutta la settimana, weekend inclusi.`,
    sondaggi: [],
  },
  {
    id: 3,
    code: 'Q03',
    tag: 'Ospitalità',
    title: 'Gli ospiti in casa',
    text: `Vi piace avere ospiti? Ogni quanto li invitate?`,
    sondaggi: [],
  },
  {
    id: 4,
    code: 'Q04',
    hasTextbox: false,
    tag: 'Sensi',
    title: 'Il corpo, i sensi, la natura',
    text: `Ora vorrei che pensiate alle sensazioni che vorreste provare nella vostra nuova casa.`,
    sondaggi: [
      'LUCE — volete svegliarvi con la luce o al buio? La sera, luce forte o soffusa? Luce calda o fredda?',
      'ARIA E TEMPERATURA — soffrite più il caldo o il freddo?',
      'MATERIALI E TATTO — vi mettono a vostro agio i materiali caldi (legno, tessuto) o freddi (pietra, metallo)? Camminereste a piedi nudi?',
      'COLORI — quali vi calmano, quali vi disturbano?',
      'SUONI — un suono di casa che amate? Uno che vi infastidisce?',
      'VERDE E ACQUA — volete piante in casa? È importante vedere il verde dalle finestre? Vi piacerebbe sentire o vedere acqua?',
      'VISTA vs RIFUGIO — preferite spaziare con lo sguardo o gli ambienti raccolti e protetti?',
    ],
  },
  {
    id: 5,
    code: 'Q05',
    tag: 'Sogni nel cassetto',
    title: 'Il cassetto dei sogni',
    text: `Apriamo il cassetto dei sogni. Immaginate di non avere limiti, né di fattibilità né di spesa. Cosa sognereste di avere nella vostra nuova casa?`,
    sondaggi: [],
  },
  {
    id: 6,
    code: 'Q06',
    tag: 'Paure',
    title: 'Cosa evitare',
    text: `Cosa è andato storto nelle ristrutturazioni precedenti, o che avete sentito da altre esperienze, che vorreste certamente evitare?`,
    sondaggi: [],
  },
  {
    id: 7,
    code: 'Q07',
    tag: 'Rassicurazioni',
    title: 'Le garanzie nero su bianco',
    text: `Cosa vorreste che vi fosse garantito, mettendolo nero su bianco, per farvi stare sereni e darvi fiducia e sicurezza?`,
    sondaggi: [],
  },
  {
    id: 8,
    code: 'Q08',
    hasTextbox: false,
    tag: 'Check list',
    title: 'Preferenze tecniche e architettoniche',
    text: `Ora vi chiedo delle preferenze su alcuni elementi architettonici e tecnologici, in modo da poter orientare le scelte dei nostri architetti.`,
    sondaggi: [
      'CUCINA-SOGGIORNO — Open space? Isola o penisola?',
      'PARETI — Boiserie / Carte da parati / Colori / Rivestimenti?',
      'PAVIMENTO — In legno (in che formato e quale essenza?) o in ceramica a grande formato?',
      'BAGNO — Doccia walk-in o vasca? WC silenziosi? Lavabo doppio o singolo? Colori delle piastrelle?',
      'SERRAMENTI ESTERNI — In PVC, alluminio, legno o legno-alluminio?',
      'PORTE INTERNE — A tutta altezza o normali? A filo muro?',
      'BATTISCOPA — Alti, bassi o senza battiscopa?',
      'CLIMATIZZAZIONE — Estiva e invernale? Riscaldamento a pavimento? Impianto centralizzato o a split? Pompe di calore? VMC?',
      'DOMOTICA ed ENERGIA — Impianto gestibile da App? Wall box? Fotovoltaico? Accumulo?',
      'SICUREZZA — Telecamere / Allarmi / Serrature / Porte blindate?',
      "ARREDO — Volete proposte d'arredo o preferite rivolgervi a terzi?",
      'ILLUMINAZIONE — luce calda o neutra? Punti luce a soffitto, sospensioni o luce diffusa/indiretta (velette, strip led)? Scenari regolabili (dimmer, controllo da app)? Lampade di design come elementi caratterizzanti?',
    ],
  },
  {
    id: 9,
    code: 'Q09',
    hasTextbox: false,
    tag: 'Domanda visiva',
    title: 'Domanda visiva',
    text: '',
    sondaggi: [],
  },
  {
    id: 10,
    code: 'Q10',
    tag: 'Giro della casa',
    title: 'Stanza per stanza',
    text: `Adesso facciamo un giro nella vostra nuova casa, una stanza alla volta. Per ognuna ditemi la cosa più importante: cosa deve farvi sentire, cosa deve permettervi di fare, cosa non può mancare…`,
    sondaggi: [
      'INGRESSO — cosa vi deve «dire» appena entrate?',
      'CUCINA — il centro della casa o un servizio? Aperta o separata? La cosa che non può mancare?',
      'ZONA GIORNO — per stare insieme, per ospitare, per i vostri riti della sera?',
      'CORRIDOIO — solo passaggio o spazio da vivere e valorizzare?',
      'CAMERE — la vostra e quelle dei figli: la priorità di ciascuna?',
      'BAGNI — quanti, e cosa vi serve davvero (anche solo «che funzionino bene»)?',
      'LAVORO E CREATIVITÀ — dove disegnare, lavorare, fare con le mani?',
      "RIFUGIO — c'è un angolo che vorreste solo vostro?",
      'STORAGE — cosa deve «sparire» e ritrovarsi senza fatica?',
      'ESTERNO — terrazzi, portici, giardino, orto, piscina, garage o car port?',
      'IL VOSTRO SEGNO — cosa dovete poter cambiare, appendere, spostare o trasformare nel tempo, perché la casa resti vostra?',
    ],
  },
];

export const EMPTY_PROMPTS: IPrompts = {
  paraverbal: '',
  architetto: '',
  cliente: '',
  merge: '',
};

// Seed/fallback: item vuoti, compilati via /admin (Supabase è l'unica sorgente reale).
export const DEFAULT_INDICATORS: IIndicators = {
  iip: Array.from({ length: 10 }, () => ({
    text: '',
    active: true,
    dimensione: '',
    area: '',
    textC: '',
    punteggio: 'crescente',
  })),
  ibap: Array.from({ length: 15 }, () => ({
    text: '',
    active: true,
    dimensione: '',
    area: '',
    componente: '',
    punteggio: 'crescente',
  })),
};

export const mkAmbiente = (tipo: string): IAmbiente => ({
  tipo,
  nome: '',
  mq: '',
  mqSerramenti: '',
  altezza: '',
  esposizione: '',
  note: '',
  openSpace: false,
  coinvolto: false,
});

// Scheda Abitativa vuota.
export const mkScheda = (): IScheda => ({
  tipologia: '',
  statoIntervento: '',
  epoca: '',
  livelli: '',
  cantina: '',
  superficie: '',
  esposizioneFronti: '',
  oreSole: '',
  ambientiBui: '',
  aperture: '',
  nuoveAperture: '',
  muri: '',
  solette: '',
  scarichiCucina: '',
  scarichiBagni: '',
  caldaia: '',
  elettrico: '',
  idraulico: '',
  giardino: '',
  terrazzo: '',
  rapportoDentroFuori: '',
  contesto: '',
  rumoriMolestiEsterni: '',
  viciniRumorosi: '',
  qualitaAria: '',
  viPrivacy: '',
  vincoliFissi: '',
  vincoliSemifissi: '',
  gradiLiberta: '',
  noteLibere: '',
  altrePriorita: '',
  ambienti: [],
  impianti: [],
  impiantiOpzioni: {},
  priorita: {
    altezze: false,
    partizioni: false,
    scarichi: false,
    aperture: false,
    impianti: false,
    cantina: false,
    orientamento: false,
    vicinato: false,
  },
  drv: {},
});

// Array di risposte vuote, una per domanda (con una sub-risposta vuota per ogni spunto).
export const mkAns = (questions: IQuestion[]): IAnswer[] =>
  questions.map((q) => ({
    transcript: '',
    chips: [],
    notes: '',
    analyzed: false,
    subAnswers: q.sondaggi.map(() => ({ transcript: '', chips: [], analyzed: false })),
  }));
